import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {TranslocoService} from '@jsverse/transloco';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {
  ArchitecturalPrincipleDirectus,
  DirectusService,
  HostingLocation,
  HrTool,
} from '@frontend/directus';
import {firstValueFrom, forkJoin, Observable, of} from 'rxjs';
import {RfiParticipatingTool, DirectusCatalog} from './step-5-3-rfi-invitation-list.types';
import {
  ParticipatingToolControllerService,
  ParticipatingToolResponse,
  ProjectChapterType,
  ProjectCoverageDataControllerService,
  ProjectCoverageDataResponse,
  ProjectStepType,
  TenderPhaseType,
} from '@frontend/shared-api';
import {RfiCoverageCalculationService} from './rfi-coverage-calculation.service';
import {RfiCoverageDisplayService} from './rfi-coverage-display.service';
import {RfiInvitationListFilterService} from './rfi-invitation-list-filter.service';
import {InvitationTableComponent} from '../../../../../../shared/components/table/invitation-table/invitation-table.component';
import {
  InvitationTableColumn,
  InvitationTableTemplateContext,
  InviteToolsResponseOf,
  PhaseInvitationConfig,
} from '../../../../../../shared/components/table/invitation-table/invitation-table.types';
import {RfiCoverageAnalysisDialogComponent} from './rfi-coverage-analysis-dialog/rfi-coverage-analysis-dialog.component';
import {AnalysisTab} from './rfi-coverage-analysis-dialog/rfi-coverage-analysis-dialog.types';
import {RfiCoverageCellComponent} from './rfi-coverage-cell/rfi-coverage-cell.component';
import {RfiAnalysisInfoButtonComponent} from './rfi-analysis-info-button/rfi-analysis-info-button.component';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';

const ANALYSIS_TAB_BY_FIELD: Record<string, AnalysisTab> = {
  'coverage.functionsCoverage': 'extras',
  'coverage.countriesCoverage': 'countries',
};

const EMPTY_DIRECTUS_CATALOG: DirectusCatalog = {
  architecturalPrinciples: [],
  hostingLocations: [],
  moduleFunctions: [],
};

@Component({
  selector: 'app-step-5-3-rfi-invitation-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InvitationTableComponent,
    PreviousStepIncompleteNoticeComponent,
    RfiCoverageAnalysisDialogComponent,
    RfiCoverageCellComponent,
    RfiAnalysisInfoButtonComponent,
  ],
  templateUrl: './step-5-3-rfi-invitation-list.component.html',
})
export class Step53RfiInvitationListComponent extends ProjectBaseStepComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly directusService = inject(DirectusService);
  private readonly calculationService = inject(RfiCoverageCalculationService);
  private readonly coverageDisplayService = inject(RfiCoverageDisplayService);
  private readonly filterService = inject(RfiInvitationListFilterService);
  private readonly participatingToolController = inject(ParticipatingToolControllerService);
  private readonly coverageDataController = inject(ProjectCoverageDataControllerService);
  private readonly currentLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  // Directus catalogs, fixed data that is loaded once the step becomes accessible.
  private readonly directusCatalogResource = rxResource({
    params: () => this.isStepPrerequisiteCompleted(),
    stream: ({params: request}) =>
      request ? this.loadDirectusCatalog() : of(EMPTY_DIRECTUS_CATALOG),
  });
  private readonly directusCatalog = computed(
    () => this.directusCatalogResource.value() ?? EMPTY_DIRECTUS_CATALOG,
  );
  private readonly architecturalPrinciples = computed(
    () => this.directusCatalog().architecturalPrinciples,
  );
  private readonly hostingLocations = computed(() => this.directusCatalog().hostingLocations);
  protected readonly moduleFunctions = computed(() => this.directusCatalog().moduleFunctions);
  private readonly projectCoverageData = signal<ProjectCoverageDataResponse | null>(null);
  private readonly coverageCell =
    viewChild.required<TemplateRef<InvitationTableTemplateContext<RfiParticipatingTool>>>(
      'coverageCell',
    );
  private readonly analysisCountCell =
    viewChild.required<TemplateRef<InvitationTableTemplateContext<RfiParticipatingTool>>>(
      'analysisCountCell',
    );

  protected readonly analysisTool = signal<RfiParticipatingTool | null>(null);
  protected readonly analysisVisible = signal(false);
  protected readonly analysisTab = signal<AnalysisTab>('overview');
  protected readonly coverageRequirements = computed(
    () => this.projectCoverageData()?.requirements ?? [],
  );
  protected readonly countryEmployees = computed(
    () => this.projectCoverageData()?.countryEmployees ?? [],
  );

  private readonly loading = computed(
    () =>
      this.architecturalPrinciples().length === 0 ||
      this.hostingLocations().length === 0 ||
      this.moduleFunctions().length === 0,
  );
  private readonly hostingLocationTooltip = computed(() => {
    const hostingLocations = this.hostingLocations();
    const lang = this.currentLang();

    return (hostingLocations ?? [])
      .map((location) => {
        const translatedName = this.getHostingLocationName(location, lang);
        return `<strong>${location.isoCode}</strong>: ${translatedName}`;
      })
      .join('<br>');
  });

  private readonly architecturalPrincipleNames = computed(() => {
    const lang = this.currentLang();
    const names = new Map<string, string>();

    this.architecturalPrinciples()
      .filter((principle) => principle.principles.length === 1)
      .forEach((principle) =>
        names.set(
          principle.principles[0],
          this.getArchitecturalPrincipleName(principle, lang) ?? principle.principles[0],
        ),
      );

    return names;
  });

  private readonly requiredModuleFunctions = computed(() => {
    const requiredIds = new Set(
      (this.projectCoverageData()?.officeFunctionsDirectusIds ?? []).map((id) =>
        Number.parseInt(id, 10),
      ),
    );

    return this.moduleFunctions().filter((moduleFunction) => requiredIds.has(moduleFunction.id));
  });
  private readonly requiredCountryIsoCodes = computed(
    () => this.projectCoverageData()?.countryIsoCodes ?? [],
  );
  private readonly functionCoverageHeaderTooltip = computed(() =>
    this.coverageDisplayService.buildFunctionHeaderTooltip(
      this.requiredModuleFunctions(),
      this.currentLang(),
    ),
  );
  private readonly countryCoverageHeaderTooltip = computed(() =>
    this.coverageDisplayService.buildCountryHeaderTooltip(this.requiredCountryIsoCodes()),
  );

  protected readonly isStepPrerequisiteCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.PROJECT_SETUP, stepId: ProjectStepType.PROJECT_CHARTER},
      {
        chapterId: ProjectChapterType.PRINCIPLE_DEFINITION,
        stepId: ProjectStepType.ARCHITECTURAL_PRINCIPLES,
      },
    ]);
  });

  private readonly prerequisitesMet = computed(() => {
    this.stepState.chaptersWithStatuses();

    const chaptersCompleted = [
      ProjectChapterType.PROJECT_SETUP,
      ProjectChapterType.PRINCIPLE_DEFINITION,
      ProjectChapterType.ACTUAL_SYSTEMS,
      ProjectChapterType.EVALUATION_CRITERIA,
    ].every((chapterId) => this.stepState.isChapterCompleted(chapterId));

    const specificStepsCompleted = this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFI, stepId: ProjectStepType.RFI_EVALUATION_SUBCRITERIA},
      {chapterId: ProjectChapterType.RFI, stepId: ProjectStepType.RFI_INVITATION_PREVIEW},
    ]);

    return chaptersCompleted && specificStepsCompleted;
  });

  protected readonly rfiPhaseConfig = computed((): PhaseInvitationConfig<RfiParticipatingTool> => ({
    phaseType: TenderPhaseType.RFI,
    timeFrameNumber: 2,
    tableConfig: {
      columns: [
        {
          field: 'toolProvider.name',
          header: 'project.steps.invitationList.provider',
          sortable: true,
          filter: true,
          width: '25%',
        },
        {
          field: 'translatedName',
          header: 'project.steps.invitationList.tool',
          cellTooltip: (item: RfiParticipatingTool) => this.getToolPrincipleTooltip(item),
          sortable: true,
          filter: true,
          width: '25%',
        },
        {
          field: 'coverage.totalCoverage',
          header: 'project.steps.rfi.invitationList.weightedCoverage',
          template: this.coverageCell(),
          sortable: true,
          width: '14%',
        },
        {
          field: 'coverage.functionsCoverage',
          header: 'project.steps.rfi.invitationList.functionCoverage',
          headerTooltip: this.functionCoverageHeaderTooltip(),
          sortable: true,
          sortField: 'coverage.coveredFunctionCount',
          template: this.analysisCountCell(),
          width: '13%',
          valueExtractor: (item: RfiParticipatingTool) =>
            `${item.coverage?.coveredFunctionCount ?? 0} / ${item.coverage?.details.requiredFunctionIds.length ?? 0}`,
        },
        {
          field: 'coverage.countriesCoverage',
          header: 'project.steps.rfi.invitationList.countryCoverage',
          headerTooltip: this.countryCoverageHeaderTooltip(),
          sortable: true,
          sortField: 'coverage.coveredCountryCount',
          template: this.analysisCountCell(),
          width: '12%',
          valueExtractor: (item: RfiParticipatingTool) =>
            `${item.coverage?.coveredCountryCount ?? 0} / ${item.coverage?.details.requiredCountryIsoCodes.length ?? 0}`,
        },
        {
          field: 'hostingProviderLocation',
          header: 'common.hosting.location',
          headerTooltip: this.hostingLocationTooltip(),
          sortable: true,
          width: '15%',
        },
        {
          field: 'selection',
          header: 'project.steps.invitationList.selection',
          sortable: true,
          sortField: 'selection',
          width: '15%',
        },
        {
          field: 'status',
          header: 'project.steps.invitationList.status',
          sortable: true,
          sortField: 'status',
          width: '15%',
        },
      ],
      quickFilter: {
        options: [
          {value: 'all', labelKey: 'project.steps.rfi.invitationList.quickFilter.all'},
          {value: 'gaps', labelKey: 'project.steps.rfi.invitationList.quickFilter.gaps'},
          {value: 'full', labelKey: 'project.steps.rfi.invitationList.quickFilter.full'},
        ],
        matches: (item: RfiParticipatingTool, value: string) =>
          value === 'all' || (value === 'gaps') === (item.coverage?.hasGaps ?? false),
        searchText: (item: RfiParticipatingTool) =>
          `${item.toolProvider.name} ${item.translatedName ?? item.name}`,
        searchPlaceholderKey: 'project.steps.rfi.invitationList.searchPlaceholder',
        hintKey: 'project.steps.rfi.invitationList.coverageHint',
      },
      emptyMessage: 'project.steps.rfi.invitationList.noData',
      defaultSortField: 'coverage.totalCoverage',
      defaultSortOrder: -1,
    },
    timeframeConfig: {
      tooltipKeys: {
        plannedShippingDate: 'project.steps.rfi.invitationList.plannedShippingDate',
        plannedSubmissionDate: 'project.steps.rfi.invitationList.plannedSubmissionDate',
      },
    },
    prerequisiteChecker: () => this.prerequisitesMet(),
    stepLockedFn: () => this.isCurrentStepLocked(),
    isLoadingFn: () => this.loading(),
    dataLoader: (projectId) => this.loadRfiData(projectId),
    translationKeys: {
      title: 'project.steps.rfi.invitationList.title',
    },
  }));

  constructor() {
    super();

    effect(() => {
      if (this.isStepPrerequisiteCompleted() && !this.projectCoverageData()) {
        void this.loadProjectCoverageData();
      }
    });
  }

  protected analysisTabFor(column: InvitationTableColumn<RfiParticipatingTool>): AnalysisTab {
    return ANALYSIS_TAB_BY_FIELD[column.field] ?? 'overview';
  }

  protected openAnalysis(tool: RfiParticipatingTool, tab: AnalysisTab = 'overview'): void {
    this.analysisTool.set(tool);
    this.analysisTab.set(tab);
    this.analysisVisible.set(true);
  }

  private loadDirectusCatalog(): Observable<DirectusCatalog> {
    return forkJoin({
      architecturalPrinciples: this.directusService.loadArchitecturalPrinciples(),
      hostingLocations: this.directusService.loadHostingLocations(),
      moduleFunctions: this.directusService.loadModuleFunctions(),
    });
  }

  private async loadProjectCoverageData(): Promise<void> {
    const coverageData = await firstValueFrom(
      this.coverageDataController.getDataForCalculation({projectId: this.projectId}),
    );

    this.projectCoverageData.set(coverageData);
  }

  private async loadRfiData(
    projectId: number,
  ): Promise<InviteToolsResponseOf<RfiParticipatingTool>> {
    // The tools already saved for this phase.
    const participatingToolsResponse = await firstValueFrom(
      this.participatingToolController.getAllParticipatingTools({
        projectId: projectId,
        phase: TenderPhaseType.RFI,
      }),
    );

    const filterResult = await this.filterService.loadInvitableTools(projectId);

    // Adds the coverage plus the saved selection and status to every remaining tool.
    const toolItems = filterResult.invitableTools.map((tool) =>
      this.mapToParticipatingTool(
        tool,
        filterResult.coverageData,
        participatingToolsResponse.tools,
      ),
    );

    return {
      tools: toolItems,
      currentPhase: participatingToolsResponse.currentPhase,
      maxAllowedParticipants: participatingToolsResponse.maxAllowedParticipants,
      calculatedPhaseSubmissionDate: participatingToolsResponse.calculatedPhaseSubmissionDate,
    };
  }

  private getToolPrincipleTooltip(tool: RfiParticipatingTool): string {
    const principle = tool.architecturalPrinciple ?? '';

    return this.architecturalPrincipleNames().get(principle) ?? principle;
  }

  private getArchitecturalPrincipleName(
    principle: ArchitecturalPrincipleDirectus,
    lang: string,
  ): string | undefined {
    return principle.translations?.find((item) => item.language === lang)?.name?.trim();
  }

  private getHostingLocationName(
    location: HostingLocation | undefined,
    lang: string,
  ): string | undefined {
    return location?.translations?.find((item) => item.language === lang)?.name?.trim();
  }

  private mapToParticipatingTool(
    tool: HrTool,
    coverageData: ProjectCoverageDataResponse,
    savedParticipatingTools: ParticipatingToolResponse[],
  ): RfiParticipatingTool {
    const lang = this.transloco.getActiveLang();
    const translation = tool.translations.find((t) => t.language === lang);
    const savedParticipatingTool = savedParticipatingTools.find(
      (pt) => pt.hrToolDirectusId === tool.id,
    );

    const rfiCoverage = this.calculationService.calculateDetailedCoverage(coverageData, tool);

    return {
      ...tool,
      translatedName: translation?.name?.trim() ? translation.name : tool.name,
      coverage: rfiCoverage,
      participatingToolId: savedParticipatingTool?.id,
      selection: savedParticipatingTool?.rfiSelection ?? null,
      status: savedParticipatingTool?.rfiStatus,
      resultReview: undefined,
    };
  }
}
